import { ServiceTitanIconLink } from "@/core/components/service-titan-icon-link";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import {
  ConfidenceLabel,
  ManualJobMatchButton,
  StatusBadge,
} from "@/accounts-payable/common";
import type { ApReceipt } from "@/accounts-payable/types";
import {
  canManuallyMatchJob,
  formatApDate,
  invoiceStatusMeta,
  receiptPaymentLabel,
  stJobUrl,
} from "@/accounts-payable/utils";
import { formatCentsOrDashIfZero } from "@/core/utils/money";

/**
 * The receipt review page's title block: store, status and match confidence,
 * then the receipt's number, job (or register PO), date, payment and total,
 * with the manual job match beside them. The receipt counterpart of
 * `InvoiceReviewHeader`.
 */
export const ReceiptReviewHeader = ({
  receipt,
  businessId,
}: {
  receipt: ApReceipt;
  businessId: string | null;
}) => {
  const payment = receiptPaymentLabel(receipt);
  return (
    <Stack gap="xs" fullWidth>
      <Stack direction="row" gap="sm" align="center" className="flex-wrap">
        <Typography variant="titleW3">{receipt.vendor_name}</Typography>
        <StatusBadge meta={invoiceStatusMeta(receipt)} />
        {receipt.st_job_external_id && receipt.job_match_confidence != null && (
          <ConfidenceLabel value={receipt.job_match_confidence} />
        )}
      </Stack>
      <Stack
        direction="row"
        gap="sm"
        align="center"
        className="flex-wrap text-md text-tertiary-text"
      >
        <span>{receipt.receipt_number ?? "No receipt number"}</span>
        {receipt.st_job_external_id ? (
          <>
            <span aria-hidden>·</span>
            <ServiceTitanIconLink
              url={stJobUrl(receipt.st_job_external_id)}
              label={`Open job #${receipt.st_job_external_id} in ServiceTitan`}
              className="ml-0 underline underline-offset-2"
            >
              Job #{receipt.st_job_external_id}
            </ServiceTitanIconLink>
          </>
        ) : (
          receipt.po_number && (
            <>
              <span aria-hidden>·</span>
              <span>PO / job: {receipt.po_number}</span>
            </>
          )
        )}
        <span aria-hidden>·</span>
        <span>{formatApDate(receipt.receipt_date)}</span>
        {payment && (
          <>
            <span aria-hidden>·</span>
            <span>{payment}</span>
          </>
        )}
        <span className="mx-1 h-5 w-px bg-stroke-tertiary" aria-hidden />
        <span className="font-semibold tabular-nums text-primary-text">
          {formatCentsOrDashIfZero(receipt.total_amount_cents)}
        </span>
        {canManuallyMatchJob(receipt) && (
          <ManualJobMatchButton
            businessId={businessId}
            documentId={receipt.id}
            documentType="receipt"
            agentRunId={receipt.agent_run_id}
            label={receipt.st_job_external_id ? "Change job" : undefined}
            className="ml-2"
          />
        )}
      </Stack>
    </Stack>
  );
};
